import { useEffect, useRef } from "react";
import { setMetaTag, setLinkTag, setJsonLd, injectExternalScript, buildBreadcrumbsForPath, type Restorer } from "@/lib/dom-meta";

const BASE = (import.meta as any).env?.BASE_URL || "/";

export interface PlatformSeoSettings {
  metaTitle: string;
  metaTitleEn: string;
  metaDescription: string;
  metaDescriptionEn: string;
  metaKeywords: string;
  metaKeywordsEn: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogType: string;
  twitterHandle: string;
  faviconUrl: string;
  themeColor: string;
  language: string;
  author: string;
  organizationName: string;
  organizationLogo: string;
  organizationSameAs: string[];
  softwareAppName: string;
  softwareAppDescription: string;
  softwareAppPrice: string;
  softwareAppCurrency: string;
  customJsonLd: unknown | null;
  customHeadCode: string;
  ga4Id: string;
  gtmId: string;
  facebookPixelId: string;
  snapchatPixelId: string;
  tiktokPixelId: string;
  pinterestPixelId: string;
  hreflangEnabled: boolean;
  // Phase 2 — advanced SEO
  llmsTxt?: string;
  faqJsonLd?: unknown | null;
  localBusinessJsonLd?: unknown | null;
  aggregateRatingJsonLd?: unknown | null;
  enableBreadcrumbsJsonLd?: boolean;
  enableImageSitemap?: boolean;
  ogImageTemplateUrl?: string;
  useCloudflareZaraz?: boolean;
}

export interface PlatformSeoPage {
  id: number;
  path: string;
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogType: string;
  canonicalUrl: string;
  noindex: boolean;
  nofollow: boolean;
  customJsonLd: unknown | null;
  customHeadCode: string;
}

interface PlatformSeoBundle {
  settings: PlatformSeoSettings | null;
  pages: PlatformSeoPage[];
}

let cache: PlatformSeoBundle | null = null;
let inflight: Promise<PlatformSeoBundle | null> | null = null;

async function loadBundle(): Promise<PlatformSeoBundle | null> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${BASE}api/platform/seo/public`, { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();
      cache = data as PlatformSeoBundle;
      return cache;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function invalidatePlatformSeoCache() {
  cache = null;
}

function findPageOverride(pages: PlatformSeoPage[], path: string): PlatformSeoPage | null {
  // exact match first
  const exact = pages.find((p) => p.path === path);
  if (exact) return exact;
  // wildcard like /blog/* matches /blog/foo
  for (const p of pages) {
    if (p.path.endsWith("/*")) {
      const prefix = p.path.slice(0, -2);
      if (path === prefix || path.startsWith(`${prefix}/`)) return p;
    }
  }
  return null;
}

function injectAnalytics(s: PlatformSeoSettings) {
  // Platform kill-switch: when Cloudflare Zaraz manages tag injection
  // for the entire platform, skip every inline pixel here to avoid
  // double-firing events alongside Zaraz's own injected scripts.
  if (s.useCloudflareZaraz) return;
  if (s.ga4Id) {
    if (
      injectExternalScript({
        id: `ga4-loader-${s.ga4Id}`,
        src: `https://www.googletagmanager.com/gtag/js?id=${s.ga4Id}`,
      })
    ) {
      injectExternalScript({
        id: `ga4-init-${s.ga4Id}`,
        inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${s.ga4Id}');`,
      });
    }
  }
  if (s.gtmId) {
    injectExternalScript({
      id: `gtm-${s.gtmId}`,
      inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.gtmId}');`,
    });
  }
  if (s.facebookPixelId) {
    injectExternalScript({
      id: `fbq-${s.facebookPixelId}`,
      inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.facebookPixelId}');fbq('track','PageView');`,
    });
  }
  if (s.tiktokPixelId) {
    injectExternalScript({
      id: `ttq-${s.tiktokPixelId}`,
      inline: `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${s.tiktokPixelId}');ttq.page();}(window, document, 'ttq');`,
    });
  }
  if (s.snapchatPixelId) {
    injectExternalScript({
      id: `snaptr-${s.snapchatPixelId}`,
      inline: `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');snaptr('init','${s.snapchatPixelId}');snaptr('track','PAGE_VIEW');`,
    });
  }
  if (s.pinterestPixelId) {
    injectExternalScript({
      id: `pintrk-${s.pinterestPixelId}`,
      inline: `!function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var n=window.pintrk;n.queue=[],n.version="3.0";var t=document.createElement("script");t.async=!0,t.src=e;var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}('https://s.pinimg.com/ct/core.js');pintrk('load','${s.pinterestPixelId}');pintrk('page');`,
    });
  }
}

/**
 * Inject custom HTML into <head> under a stable scope marker. Returns a
 * cleanup that removes exactly the nodes it inserted (so per-page custom
 * head does not leak across SPA navigations). Re-calling with the same
 * scope first removes the previous nodes.
 */
function injectCustomHead(scope: string, html: string): Restorer {
  if (typeof document === "undefined" || !html.trim()) return () => {};
  const attr = "data-platform-seo-scope";
  // Remove any pre-existing nodes for this scope (defensive).
  document.head.querySelectorAll(`[${attr}="${scope}"]`).forEach((n) => n.remove());

  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const inserted: Element[] = [];
  while (tmp.firstChild) {
    const child = tmp.firstChild;
    if (child.nodeType === 1) {
      (child as Element).setAttribute(attr, scope);
      document.head.appendChild(child);
      inserted.push(child as Element);
    } else {
      tmp.removeChild(child);
    }
  }
  return () => {
    for (const el of inserted) {
      try { el.remove(); } catch {}
    }
  };
}

// Apply a platform-level URL redirect for the current path BEFORE rendering
// any SEO tags. Returns a promise that resolves once the check is complete.
// If a redirect was applied via `window.location.replace`, the promise will
// not resolve in practice (the page navigates away).
// Per-tab hop limit prevents A→B→A loops or longer chains caused by
// misconfigured admin rules. We use sessionStorage so reloading the tab
// resets the count, and a small constant (3) gives admins room to chain a
// couple of legitimate hops before the client gives up.
const REDIRECT_HOP_KEY = "nx_pf_redirect_hops";
const REDIRECT_MAX_HOPS = 3;
async function maybeApplyPlatformRedirect(path: string): Promise<void> {
  if (typeof window === "undefined") return;
  let hops = 0;
  try {
    hops = Number(sessionStorage.getItem(REDIRECT_HOP_KEY) || "0") || 0;
  } catch { /* sessionStorage may be unavailable in privacy modes */ }
  if (hops >= REDIRECT_MAX_HOPS) {
    // We've already redirected too many times this session — bail out.
    try { sessionStorage.removeItem(REDIRECT_HOP_KEY); } catch { /* noop */ }
    return;
  }
  try {
    const res = await fetch(`${BASE}api/platform/redirects/resolve?path=${encodeURIComponent(path)}`, {
      credentials: "include",
    });
    if (!res.ok) return;
    const data = await res.json().catch(() => null) as { match?: { targetPath: string; statusCode: number } | null } | null;
    const target = data?.match?.targetPath;
    if (!target || target === path) {
      try { sessionStorage.removeItem(REDIRECT_HOP_KEY); } catch { /* noop */ }
      return;
    }
    // Build absolute URL if target starts with /, otherwise treat as full URL.
    // (The server has already verified that http(s) targets stay on the
    // platform domain — see validateRedirectTarget on the API side.)
    const dest = target.startsWith("http") ? target : `${window.location.origin}${target.startsWith("/") ? "" : "/"}${target}`;
    try { sessionStorage.setItem(REDIRECT_HOP_KEY, String(hops + 1)); } catch { /* noop */ }
    window.location.replace(dest);
  } catch {
    // network errors are non-fatal — render the page normally.
  }
}

export function usePlatformSeo(path: string, enabled: boolean) {
  const cleanupsRef = useRef<Restorer[]>([]);

  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    let cancelled = false;
    let cleanups: Restorer[] = [];

    // Fire-and-forget redirect check. If it matches, the browser navigates
    // away before SEO injection completes — safe.
    void maybeApplyPlatformRedirect(path);

    (async () => {
      const bundle = await loadBundle();
      if (cancelled || !bundle?.settings) return;

      const s = bundle.settings;
      const lang = s.language || "ar";
      const isAr = lang === "ar";
      const override = findPageOverride(bundle.pages || [], path);

      // <html lang> + dir
      try {
        document.documentElement.setAttribute("lang", lang);
        document.documentElement.setAttribute("dir", isAr ? "rtl" : "ltr");
      } catch {}

      // Title
      const title = (override?.title || (isAr ? s.metaTitle : s.metaTitleEn) || "Nexus Cart").trim();
      document.title = title;

      // Description / keywords
      const description = (override?.description || (isAr ? s.metaDescription : s.metaDescriptionEn) || "").trim();
      const keywords = (override?.keywords || (isAr ? s.metaKeywords : s.metaKeywordsEn) || "").trim();
      cleanups.push(setMetaTag("description", description));
      if (keywords) cleanups.push(setMetaTag("keywords", keywords));
      if (s.author) cleanups.push(setMetaTag("author", s.author));
      if (s.themeColor) cleanups.push(setMetaTag("theme-color", s.themeColor));

      // Robots
      const robotsVals: string[] = [];
      if (override?.noindex) robotsVals.push("noindex");
      if (override?.nofollow) robotsVals.push("nofollow");
      if (robotsVals.length > 0) cleanups.push(setMetaTag("robots", robotsVals.join(", ")));
      else cleanups.push(setMetaTag("robots", "index, follow"));

      // Canonical
      const origin = window.location.origin;
      const canonical = (override?.canonicalUrl || `${origin}${path}`).trim();
      cleanups.push(setLinkTag("canonical", canonical));

      // hreflang (if both ar/en titles exist + enabled)
      if (s.hreflangEnabled) {
        cleanups.push(setLinkTag("alternate", canonical, { hreflang: "ar" }));
      }

      // Open Graph
      const ogTitle = (override?.ogTitle || s.ogTitle || title).trim();
      const ogDescription = (override?.ogDescription || s.ogDescription || description).trim();
      const ogImage = (override?.ogImage || s.ogImage || "").trim();
      const ogType = (override?.ogType || s.ogType || "website").trim();
      cleanups.push(setMetaTag("og:title", ogTitle, "property"));
      cleanups.push(setMetaTag("og:description", ogDescription, "property"));
      cleanups.push(setMetaTag("og:type", ogType, "property"));
      cleanups.push(setMetaTag("og:url", canonical, "property"));
      if (ogImage) cleanups.push(setMetaTag("og:image", ogImage, "property"));
      if (s.organizationName) cleanups.push(setMetaTag("og:site_name", s.organizationName, "property"));

      // Twitter
      cleanups.push(setMetaTag("twitter:card", ogImage ? "summary_large_image" : "summary"));
      cleanups.push(setMetaTag("twitter:title", ogTitle));
      cleanups.push(setMetaTag("twitter:description", ogDescription));
      if (ogImage) cleanups.push(setMetaTag("twitter:image", ogImage));
      if (s.twitterHandle) cleanups.push(setMetaTag("twitter:site", s.twitterHandle));

      // Favicon
      if (s.faviconUrl) cleanups.push(setLinkTag("icon", s.faviconUrl));

      // JSON-LD: Organization (always — overrides static index.html default)
      if (s.organizationName) {
        cleanups.push(
          setJsonLd("platform-organization", {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: s.organizationName,
            url: origin,
            logo: s.organizationLogo || undefined,
            sameAs: Array.isArray(s.organizationSameAs) ? s.organizationSameAs : undefined,
          }),
        );
      }
      // JSON-LD: WebSite (with SearchAction)
      cleanups.push(
        setJsonLd("platform-website", {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: s.organizationName || "Nexus Cart",
          url: origin,
        }),
      );
      // JSON-LD: SoftwareApplication (only on root)
      if (path === "/" && s.softwareAppName) {
        cleanups.push(
          setJsonLd("platform-softwareapp", {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: s.softwareAppName,
            description: s.softwareAppDescription || description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: s.softwareAppPrice || "0",
              priceCurrency: s.softwareAppCurrency || "SAR",
            },
          }),
        );
      }
      // JSON-LD: BreadcrumbList (auto from path, when enabled)
      if (s.enableBreadcrumbsJsonLd !== false) {
        const homeName = isAr ? "الرئيسية" : "Home";
        const breadcrumb = buildBreadcrumbsForPath(origin, path, homeName);
        if (breadcrumb) {
          cleanups.push(setJsonLd("platform-breadcrumbs", breadcrumb));
        }
      }
      // JSON-LD: FAQPage (admin-managed, applies to listed paths or all if no `appliesTo`)
      if (s.faqJsonLd && typeof s.faqJsonLd === "object") {
        const faq = s.faqJsonLd as { appliesTo?: string[] };
        const ok = !Array.isArray(faq.appliesTo) || faq.appliesTo.includes(path);
        if (ok) cleanups.push(setJsonLd("platform-faq", s.faqJsonLd));
      }
      // JSON-LD: LocalBusiness (admin-managed)
      if (s.localBusinessJsonLd && typeof s.localBusinessJsonLd === "object") {
        cleanups.push(setJsonLd("platform-local-business", s.localBusinessJsonLd));
      }
      // JSON-LD: AggregateRating (admin-managed; usually attached to the
      // SoftwareApplication/Organization). We expose it as a separate node
      // so the admin can author it freely.
      if (s.aggregateRatingJsonLd && typeof s.aggregateRatingJsonLd === "object") {
        cleanups.push(setJsonLd("platform-aggregate-rating", s.aggregateRatingJsonLd));
      }
      // Custom JSON-LD (settings-level, applies everywhere)
      if (s.customJsonLd) {
        cleanups.push(setJsonLd("platform-custom-global", s.customJsonLd));
      }
      // Per-page custom JSON-LD
      if (override?.customJsonLd) {
        cleanups.push(setJsonLd(`platform-custom-page-${override.id}`, override.customJsonLd));
      }

      // Custom head HTML — scoped + revertible so per-page injections do
      // not bleed into other routes during SPA navigation.
      cleanups.push(injectCustomHead("settings", s.customHeadCode || ""));
      cleanups.push(injectCustomHead("page", override?.customHeadCode || ""));

      // Analytics scripts (idempotent — only injected once)
      injectAnalytics(s);

      // Track for next location change
      cleanupsRef.current = cleanups;
    })();

    return () => {
      cancelled = true;
      // Restore the previous values so navigations between pages don't pile up.
      for (const fn of cleanups) {
        try {
          fn();
        } catch {}
      }
      cleanups = [];
    };
  }, [path, enabled]);
}
